import React, { useState, useEffect, useRef } from 'react';

interface FeatureItem {
  title: string;
  description: string;
  icon: React.ReactElement;
  gradient: string;
  detailedDescription?: string;
}

const Features: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [hoveredFeature, setHoveredFeature] = useState<number | null>(null);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const features: FeatureItem[] = [
    {
      title: 'Lightning Fast Performance',
      description: 'SSD storage and optimized servers ensure your website loads in milliseconds.',
      detailedDescription: 'Advanced caching, CDN integration, and high-performance SSDs deliver blazing-fast loading times',
      icon: <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>,
      gradient: 'from-yellow-500 to-orange-500'
    },
    {
      title: '99.99% Uptime Guarantee',
      description: 'Rock-solid infrastructure with guaranteed uptime and 24/7 monitoring.',
      detailedDescription: 'Enterprise-grade infrastructure with redundant systems and proactive monitoring',
      icon: <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>,
      gradient: 'from-green-500 to-emerald-500'
    },
    {
      title: 'Advanced SSL Security',
      description: 'Free SSL certificates and advanced security features to protect your data.',
      detailedDescription: 'Multi-layered security with DDoS protection, malware scanning, and SSL encryption',
      icon: <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>,
      gradient: 'from-blue-500 to-indigo-500'
    },
    {
      title: 'Automated Daily Backups',
      description: 'Automated daily backups ensure your data is always safe and recoverable.',
      detailedDescription: 'Multiple backup copies stored securely with one-click restoration options',
      icon: <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" /></svg>,
      gradient: 'from-purple-500 to-violet-500'
    },
    {
      title: '24/7 Expert Support',
      description: 'Expert technical support available around the clock via chat, email, and phone.',
      detailedDescription: 'Dedicated support team with deep technical expertise ready to help anytime',
      icon: <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192L5.636 18.364M12 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>,
      gradient: 'from-pink-500 to-rose-500'
    },
    {
      title: 'Infinitely Scalable',
      description: 'Easily upgrade your hosting as your business grows with flexible scaling options.',
      detailedDescription: 'Seamless scaling with automatic resource allocation and load balancing',
      icon: <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>,
      gradient: 'from-cyan-500 to-teal-500'
    },
  ];

  return (
    <section 
      ref={sectionRef}
      className="bg-white py-20"
    >

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-4 py-2 mb-6 bg-gradient-to-r from-orange-50 to-yellow-50 border border-orange-200 rounded-full">
            <span className="w-2 h-2 bg-gradient-to-r from-orange-400 to-yellow-500 rounded-full mr-3"></span>
            <span className="text-sm font-semibold text-gray-700">Premium Features</span>
          </div>
          
          <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-6">
            Why Choose 12and22?
          </h2>
          
          <p className="text-xl text-gray-600 max-w-4xl mx-auto">
            We provide enterprise-grade hosting solutions with the reliability, 
            performance, and support your business deserves
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <div
              key={index}
              className={`group relative transform transition-all duration-700 hover:-translate-y-2 ${
                isVisible 
                  ? 'translate-y-0 opacity-100' 
                  : 'translate-y-8 opacity-0'
              }`}
              style={{ transitionDelay: `${index * 100}ms` }}
              onMouseEnter={() => setHoveredFeature(index)}
              onMouseLeave={() => setHoveredFeature(null)}
            >
              {/* Main Feature Card */}
              <div className="bg-gradient-to-br from-white via-gray-50 to-gray-100 border border-gray-200 rounded-2xl p-8 hover:shadow-xl hover:scale-105 transition-all duration-300 h-full text-center relative overflow-hidden group">
                {/* Glossy overlay */}
                <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl"></div>
                <div className="relative z-10">
                {/* Icon Container */}
                <div className="flex justify-center mb-6">
                  <div className={`w-16 h-16 ${index === 0 || index === 5 ? 'bg-gradient-to-br from-orange-500 via-yellow-500 to-orange-600' : 'bg-gradient-to-br from-primary-navy via-primary-800 to-primary-900'} rounded-2xl flex items-center justify-center shadow-lg relative overflow-hidden`}>
                    <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-white/10 to-transparent rounded-2xl"></div>
                    <div className="relative z-10">{feature.icon}</div>
                  </div>
                </div>

                {/* Content */}
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">
                    {feature.title}
                  </h3>
                  
                  {/* Main Description */}
                  <p className="text-gray-600 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-16">
          <div className="inline-flex items-center space-x-6 text-gray-600">
            <span className="text-sm font-medium">Ready to experience the difference?</span>
            <div className="flex items-center space-x-2">
              <div className="flex space-x-1">
                {[...Array(5)].map((_, i) => (
                  <svg key={i} className="w-4 h-4 text-yellow-400" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                  </svg>
                ))}
              </div>
              <span className="text-sm font-bold text-gray-900">4.9/5 from 10,000+ customers</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Features;